import { IS_PRODUCTION } from '@/lib/site'

/**
 * A page that exists but is not published.
 *
 * Fatih, 2026-09-01: *"ileride (cuma günü mesela akkök holding yayınlayacağız)
 * yeni bir sayfa oluşturacağımda live olmayan draft sayfa oluşturabiliyor olsam
 * ve drafttaki sayfayı düzenleyip oturttuktan sonra live a alabilme imkanım olsa
 * çok güzel olur."*
 *
 * **His premise was wrong and his instinct was right, which is why this exists.**
 * An AI-editor edit does not reach the live site: it pushes to `main`, `main` is
 * preview, and `production` only moves when Kagan publishes. Kagan told him so.
 * But publishing is **all or nothing** — it fast-forwards `production` to
 * whatever `main` holds — so a half-written page sitting on preview goes live the
 * moment somebody publishes for an unrelated reason. That is the real gap, and
 * no amount of care about *when* to publish closes it. This closes *what*.
 *
 * TWO FLAGS, ONE QUESTION, AND THEY ARE NOT THE SAME QUESTION:
 *
 *   draft: true   — "this page is not finished"      → Fatih, from the AI editor
 *   publish       — "what is finished should go live" → Kagan, from Excellence
 *
 * Neither substitutes for the other. Fatih says a page is ready; Kagan says the
 * site goes out. Collapsing them into one control would either let Fatih publish
 * or make Kagan the person who decides a paragraph is done.
 *
 * ---------------------------------------------------------------------------
 * WHY THE FILTER IS AT THE COLLECTION AND NOT AT EACH CONSUMER
 *
 * A page here is emitted by one of two systems — `ROUTES` for the written pages,
 * `prerenderLegacy()` for the imported pages, posts, reports, e-books and
 * stories — and *listed* by a third: the card grids on the index pages
 * (`PAGE_STORIES`, `PAGE_REPORTS`, `PAGE_EBOOKS`). Hiding a page from the build
 * but not from the grid publishes a card that opens a 404, which is worse than
 * publishing the draft.
 *
 * So the filter is applied where each collection is **exported**, once. Every
 * consumer — emitter, grid, sitemap, internal-link check — reads the filtered
 * list without knowing this feature exists, and a consumer added next year is
 * correct by default. It is the shape `LOCALES` / `ALL_LOCALES` already uses for
 * paused locales, and it fails in the same safe direction: forget the flag and
 * you get the published list, so the mistake is "did not publish" rather than
 * "published something unfinished".
 *
 * Since 2026-10-02 the imported pages and posts are drafted the same way —
 * `LEGACY_PAGES` / `ALL_LEGACY_PAGES` and `LEGACY_POSTS` / `ALL_LEGACY_POSTS` in
 * `src/legacy.ts` — so a post converted from Fatih's missing-sites package
 * (`scripts/import-missing-post.mjs`, `draft: true` by default) waits on preview
 * for its wave. `scripts/verify-drafts.mjs` checks the built output: on www no
 * page, no sitemap entry, no link and no picture of a draft.
 * ---------------------------------------------------------------------------
 */
export interface Draftable {
  /**
   * Not built for production. Absent or `false` means published.
   *
   * A page, not a locale: `draft` is per entry and applies to every language it
   * would render in. Drafting one locale of a page while another is live is a
   * second dimension nobody has asked for, and it can be added later without
   * moving this.
   */
  draft?: boolean
}

/**
 * Whether drafts are visible in this build. True on preview, false on
 * production — which is the whole mechanism.
 */
export const SHOW_DRAFTS = !IS_PRODUCTION

/** The published members of a collection: everything on preview, no drafts on www. */
export function published<T extends Draftable>(items: T[]): T[] {
  return SHOW_DRAFTS ? items : items.filter((i) => !i.draft)
}

/** What is being held back, for the build to say out loud. */
export function draftsIn<T extends Draftable & { slug?: string; id?: string }>(
  items: T[],
  label: string,
): string[] {
  return items.filter((i) => i.draft).map((i) => `${label}/${i.slug ?? i.id ?? '?'}`)
}
